/**
 * WPQ Common Utilities
 * 
 * Comprehensive shared utilities for WPQ transaction components:
 * - WPQ_General
 * - WPQ_GetDetails 
 * - Test_and_Certificate
 * 
 * Includes validation, submit logic, error handling, object creation utilities, and common styles
 * Reuses patterns from WPSCommonUtils.js for consistency
 */

import axios from "axios";
import { getPresignedURL } from "../../../Services/Welder-services";
import { Action } from "../../utility/common/constants/CommonConstants";
import { handleUnifiedSubmit } from "../../utility/common/TransactionComponents/TransactionSubmitUtils";

export { handleUnifiedSubmit };

export const emptyError = (err, seterr, iserror) => {
  let entries = Object.keys(err);
  entries.forEach((i) => {

    if (err[i].message === process.env.REACT_APP_SPECIAL_CHARACTER_MSG) {
      iserror = true
      err[i] = {
        status: true,
        message: err[i].message,
      };
    }
    else {

      err[i] = {
        status: false,
        message: ``,
      };
    }
  });

  seterr((prevState) => ({
    ...prevState,
    ...err,
  }));

  return iserror
};

export const emptyErrorWPQDetail = (err, seterr, iserror) => {
  let entries = Object.keys(err);
  entries.forEach((i) => {
    // Safety check to prevent undefined access
    if (!err[i] || !Array.isArray(err[i]) || !err[i][0] || !err[i][1] ||
      !err[i][0].Actual_Value || !err[i][1].Range_Qualified ||
      !err[i][0].Actual_Value[0] || !err[i][1].Range_Qualified[0]) {
      return; // Skip this iteration if structure is invalid
    }

    let errmessage = process.env.REACT_APP_SPECIAL_CHARACTER_MSG
    errmessage = errmessage.replace("-", "")
    if (err[i][0].Actual_Value[0].message === errmessage || err[i][1].Range_Qualified[0].message === errmessage || err[i][0].Actual_Value[0].message === "Special Characters # & % + $ ' < > = ; not allowed" || err[i][1].Range_Qualified[0].message === "Special Characters # & % + $ ' < > = ; not allowed") {
      iserror = true
      seterr((prevState) => ({
        ...prevState,
        [entries]: [
          {
            Actual_Value: [{
              status: true,
              message: process.env.REACT_APP_SPECIAL_CHARACTER_MSG
            }]
          },
          {
            Range_Qualified: [{
              status: true,
              message: process.env.REACT_APP_SPECIAL_CHARACTER_MSG
            }]
          },
        ],
      }));
    }
    else {
      seterr((prevState) => ({
        ...prevState,
        [entries]: [
          {
            Actual_Value: [{
              status: false,
              message: ""
            }]
          },
          {
            Range_Qualified: [{
              status: false,
              message: ""
            }]
          },
        ],
      }));
    }
  });
  return iserror
};

/**
 * Calculate remaining days for welder qualification
 */
export const addWeldingValidUptoDate = () => {
  return Number(process.env.REACT_APP_WELDER_QUALIFICATION_DUE_IN * 24 * 60 * 60 * 1000);
};

/**
 * Create test object for WPQ
 */
export const createWPQTestObj = (start, count, WPStype) => {
  const testarray = [];
  for (let i = start; i <= count; i++) {
    testarray.push({
      SK: "",
      PK: "",
      WPQ_WelderId: "",
      WPQ_RecordNo: "",
      WPQ_WP_TypeOfTest: "",
      WPQ_WP_Acceptance_Criteria: "",
      WPQ_WP_Result: "",
      WPQ_WP_Comments: "",
      WPQ_WP_Report_Nummber: "",
      status: "Active",
      wld_WPSType: WPStype,
    });
  }
  return testarray;
};

/**
 * Common input handler creator for WPQ
 */
export const createWPQInputHandler = (fieldName, validator, setError) => (e) => {
  const inputError = validator(e.target.value);
  setError((prevState) => ({
    ...prevState,
    [fieldName]: inputError
  }));
  return inputError.status;
};

/**
 * Common error clear handler creator for WPQ
 */
export const createWPQErrorClearHandler = (fieldName, setError) => () => {
  setError((prevState) => ({
    ...prevState,
    [fieldName]: { status: false, message: "" }
  }));
};

/**
 * Handle WPQ count for change numbers
 */
export const handleWPQCount = (action, headerObj, location, Action, Status) => {
  if (action === Action.ActionEdit) {
    if (headerObj.WPQH_Status === Status.StatusApproved) {
      headerObj.WPQ_Change_No = location.state?.WPQ_Change_No;
    }
  }
};

// ===== CONSOLIDATED FUNCTIONS FROM WPQ_common.js =====

/**
 * Tubesheet-specific error handling
 */
export const emptyErrorWPQDetailTubesheet = (err, seterr, iserror) => {
  let entries = Object.keys(err);
  entries.forEach((i) => {
    let value = err[i]
    if (Array.isArray(value)) {
      let errmessage = process.env.REACT_APP_SPECIAL_CHARACTER_MSG
      if (err[i][0].Value_A[0].message === errmessage || err[i][1].Value_B[0].message === errmessage) {
        iserror = true
        seterr((prevState) => ({
          ...prevState,
          [i]: [
            {
              Value_A: [{
                status: true,
                message: process.env.REACT_APP_SPECIAL_CHARACTER_MSG
              }]
            },
            {
              Value_B: [{
                status: true,
                message: process.env.REACT_APP_SPECIAL_CHARACTER_MSG
              }]
            },
          ],
        }));
      }
      else {
        seterr((prevState) => ({
          ...prevState,
          [i]: [
            {
              Value_A: [{
                status: false,
                message: ""
              }]
            },
            {
              Value_B: [{
                status: false,
                message: ""
              }]
            },
          ],
        }));
      }
    }
    else {
      if (err[i].message === process.env.REACT_APP_SPECIAL_CHARACTER_MSG) {
        iserror = true
        err[i] = {
          status: true,
          message: err[i].message,
        };
      }
      else {
        err[i] = {
          status: false,
          message: ``,
        };
      }
      seterr((prevState) => ({
        ...prevState,
        ...err,
      }));
    }
  });

  return iserror
};

/**
 * Create pipe-delimited range string
 */
export const createRangePipedelimited = (item) => {
  return [
    item.Range_Qualified[0][0],
    item.Range_Qualified[1][0],
    item.Range_Qualified[2][0],
    item.Range_Qualified[3][0],
    item.Range_Qualified[4][0],
    item.Range_Qualified[5][0],
    item.Range_Qualified[6][0],
  ]
    .filter((value) => value.trim() !== "")
    .join(",") + "|";
};

/**
 * Create WPQ object with dynamic key
 */
export const createWPQObject = (item, key) => ({
  wld_createdon: item.wld_createdon || "",
  wld_WPSType: item.wld_WPSType,
  WPQ_WelderId: item.WPQ_WelderId,
  WPQ_RecordNo: item.WPQ_RecordNo,
  WPQ_Process: item.WPQ_Process,
  WPQ_Process_Category: item.WPQ_Process_Category,
  WPQ_Type: item.WPQ_Type,
  [key]: item[key],
});

/**
 * Save image to S3
 */
export const saveImage = async (image, folder, setUrl, setimg, profile) => {
  if (image !== "") {
    const result = await getPresignedURL(folder, image);
    const posturl = result.url;
    setUrl(posturl);
    await axios.put(posturl, profile);
    setimg(true)
  }
};

/**
 * Get image from S3
 */
export const getImage = async (item, folder, setUrl) => {
  if (item) {
    const result = await getPresignedURL(folder, item);
    const posturl = result.url;
    setUrl(posturl);
  }
};

/**
 * Handle count increment
 */
export const handleCount = (changeno, action, Action) => {
  return isNaN(changeno) ? "1" : String(Number(changeno) + 1);
};

/**
 * Create WPQ range qualification post list
 */
export const createWPQRangeQualPostlist = (detaildata) => {
  const keys = [
    "WPQ_Detail_Backing",
    "WPQ_FM_Arc_transfermode",
    "WPQ_Detail_Type",
    "WPQ_FM_Automatic_AVC_System",
    "WPQ_FM_Welding_Position",
    "WPQ_FM_Specification",
    "WPQ_FM_Classification",
    "WPQ_FM_filler_Wire",
    "WPQ_FM_F_No",
    "WPQ_FM_Consumable_Insert",
    "WPQ_FM_Product_Form",
    "WPQ_FM_No_of_LayerDeposit",
    "WPQ_FM_Thickness_Limit",
    "WPQ_FM_Progression",
    "WPQ_FM_Current_Polarity",
    "WPQ_FM_Backing_Gas",
    "WPQ_FM_Direct_VisualControl",
    "WPQ_FM_Automatic_jointTracking",
    "WPQ_FM_Single_Pass_PerSide",
    "WPQ_FM_Automatic_AVC_System",
    "WPQ_FM_Weld_Deposit_Thickness",
    "WPQ_Groove_610",
    "WPQ_Groove_73to610",
    "WPQ_Groove_73",
    "WPQ_Fillet_610",
    "WPQ_Fillet_73to610",
    "WPQ_Fillet_73",
    "WPQ_Overlay_610",
    "WPQ_Overlay_73to610",
    "WPQ_Overlay_73",
  ];

  const postList = [];
  for (const item of detaildata) {
    if (item.WPQ_Process != undefined && item.WPQ_Process != "") {
      keys.forEach((key) => {
        postList.push(createWPQObject(item, key));
      });
    }
  }
  return postList
};

/**
 * Set error for range and actual fields
 */
export const setErrorRangeAndActual = (setError, column, msg) => {
  setError((prevState) => ({
    ...prevState,
    [column]: [
      {
        Actual_Value: [
          {
            status: true,
            message: msg,
          },
        ],
      },
      {
        Range_Qualified: [
          {
            status: true,
            message: msg,
          },
        ],
      },
    ],
  }));
};

/**
 * Backward compatibility alias
 */
export { addWeldingValidUptoDate as addWedlingValidUptoDate };

// ===== ADDITIONAL EXPORTS FOR BACKWARD COMPATIBILITY =====
// These functions were previously in WPQ_common.js and are now consolidated here

/**
 * Handle count increment (simplified version for backward compatibility)
 * @param {string|number} changeno - Current change number
 * @returns {string} - Incremented change number
 */
export const handleCountSimple = (changeno) => {
  return isNaN(changeno) ? "1" : String(Number(changeno) + 1);
};

/**
 * WPQ-specific submit handlers matching WPS pattern
 */
export const handleWPQGeneralSubmit = (validate, handleAdd, setLoading, setIsEdited, status) => {
  return handleUnifiedSubmit({
    validate,
    handleAdd,
    setLoading,
    setIsEdited,
    status
  });
};

export const handleWPQGeneralSubmitTemp = (IsEdited, validateTemp, handleAdd, setLoading, setIsEdited, status) => {
  return handleUnifiedSubmit({
    isTemp: true,
    isEdited: IsEdited,
    validate: validateTemp,
    handleAdd,
    setLoading,
    setIsEdited,
    status
  });
};

/**
 * Common field disable logic for WPQ
 */
export const DisableWPQFields = (actualStatus, preparedBy, Status, Action, action) => {
  if (action === Action.ActionCopy) {
    return true; // Allow editing for copy
  } else if (actualStatus === Status.StatusPending && (sessionStorage.getItem("Band") !== "P" && sessionStorage.getItem("Band") !== "T")) {
    return true;
  } else {
    return false;
  }
};

/**
 * Common header change handler for WPQ
 */
export const handleWPQHeaderChange = (e, stateConfig, actionConfig, err) => {
  const { setheaderObj, setError } = stateConfig;
  const { action, setIsEdited } = actionConfig;

  if (action === Action.ActionEdit) setIsEdited(true);

  const { name, value } = e.target;
  setheaderObj((prevState) => ({
    ...prevState,
    [name]: value,
  }));

  if (err[name] && !String(err[name].message) == "Special") {
    setError((prevState) => ({
      ...prevState,
      [name]: { status: false, message: "" },
    }));
  }
};

/**
 * Common error state initialization for WPQ
 */
export const initializeWPQErrorState = () => {
  const defaultError = { status: false, message: "" };

  return {
    WPQ_WelderId: { ...defaultError },
    WPQ_WelderName: { ...defaultError },
    WPQ_Date: { ...defaultError },
    WPQ_RecordNo: { ...defaultError },
    WPQ_Wps_RecordNo: { ...defaultError },
    WPQ_ReviNo: { ...defaultError },
    WPQ_Inspection: { ...defaultError },
    WPQ_DOB: { ...defaultError },
    WPQ_Qualificationcode: { ...defaultError },
    WPQ_Last_UpdatedDate: { ...defaultError },
    WPQ_Last_UpdatedBy: { ...defaultError },
    WPQ_Welded_on_coupon: { ...defaultError },
    WPQ_CompanyName: { ...defaultError },
    WPQ_Division: { ...defaultError },
    WPQ_PlantLocation: { ...defaultError },
    WPQ_specialNote: { ...defaultError },
    WPQ_Status: { ...defaultError },
    WPQ_BM_BM_ProductForm: { ...defaultError },
    WPQ_BM_BM_Spec: { ...defaultError },
    WPQ_BM_BM_Pno: { ...defaultError },
    WPQ_BM_BM_Gno: { ...defaultError },
    WPQ_BM_BM_Thk: { ...defaultError },
    WPQ_BM_BM_Dia: { ...defaultError },
    WPQ_BM_WTBM_ProductForm: { ...defaultError },
    WPQ_BM_WTBM_Spec: { ...defaultError },
    WPQ_BM_WTBM_Pno: { ...defaultError },
    WPQ_BM_WTBM_Gno: { ...defaultError },
    WPQ_BM_WTBM_Thk: { ...defaultError },
    WPQ_BM_WTBM_Dia: { ...defaultError },
    WPQ_VAR_TypeofJoint_actualValue: { ...defaultError },
    WPQ_VAR_TypeofJoint_RangeQualified: { ...defaultError },
    WPQ_VAR_BaseM_actualValue: { ...defaultError },
    WPQ_VAR_BaseM_RangeQualified: { ...defaultError },
    WPQ_VAR_Pcategory_RangeQualified: { ...defaultError },
    WPQ_BMT_Plate_thickness_ActualV_Groove: { ...defaultError },
    WPQ_BMT_Plate_thickness_ActualV_Fillet: { ...defaultError },
    WPQ_BMT_Plate_thickness_ActualV_Overlay: { ...defaultError },
    WPQ_BMT_Pipe_thickness_ActualV_Groove: { ...defaultError },
    WPQ_BMT_Pipe_thickness_ActualV_Fillet: { ...defaultError },
    WPQ_BMT_Pipe_thickness_ActualV_Overlay: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_ActualV_Groove: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_ActualV_Fillet: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_ActualV_Overlay: { ...defaultError },
    WPQ_BMT_Plate_thickness_RangeQ_Groove: { ...defaultError },
    WPQ_BMT_Plate_thickness_RangeQ_Fillet: { ...defaultError },
    WPQ_BMT_Plate_thickness_RangeQ_Overlay: { ...defaultError },
    WPQ_BMT_Pipe_thickness_RangeQ_Groove: { ...defaultError },
    WPQ_BMT_Pipe_thickness_RangeQ_Fillet: { ...defaultError },
    WPQ_BMT_Pipe_thickness_RangeQ_Overlay: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_RangeQ_Groove: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_RangeQ_Fillet: { ...defaultError },
    WPQ_BMT_Pipe_Diameter_RangeQ_Overlay: { ...defaultError },
    WPQ_WP_Process_Process: { ...defaultError },
    WPQ_WP_Process_Type: { ...defaultError },
    WPQ_WP_Type_Process: { ...defaultError },
    WPQ_WP_Type_Type: { ...defaultError },
    WPQ_Process: { ...defaultError }
  };
};